<template>
	<div class="text-input group flex flex-col">
		<label v-if="label" :class="[theme.label, labelClass]">
			<span>{{ label }}</span>
			<slot name="label-end"></slot>
		</label>
		<div class="relative flex flex-grow flex-col">
			<input
				v-if="['text', 'number', 'date', 'password', 'color'].includes(type)"
				:id="type === 'date' ? state.id : undefined"
				ref="inputEl"
				:name="name"
				:autocomplete="autocomplete"
				:list="list"
				:size="char"
				:value="formattedValue"
				:type="inputType"
				:class="['text-inner-input flex-grow', theme.input, inputDirectionClass, inputClass]"
				:style="inputStyle"
				:placeholder="theme.floating_label ? ' ' : placeholder"
				:disabled="disabled || ['date'].includes(type)"
				@input="changeEventValue"
				@keydown="(e) => emit('keydown', e)"
				@focus="(e) => emit('focus', e)"
				@blur="(e) => emit('blur', e)"
			/>
			<div
				v-if="['textarea'].includes(type)"
				:class="[
					theme.input,
					'pointer-events-none select-none overflow-x-hidden whitespace-pre-wrap opacity-0',
					inputDirectionClass,
					inputClass
				]"
				:style="[
					{
						wordWrap: 'break-word'
					},
					inputStyle || {}
				]"
				v-text="(formattedValue || '') + '.'"
			/>
			<textarea
				v-if="['textarea'].includes(type)"
				ref="inputEl"
				:name="name"
				:autocomplete="autocomplete"
				:class="[
					theme.input,
					'absolute bottom-0 left-0 right-0 top-0 resize-none',
					inputDirectionClass,
					inputClass
				]"
				:style="[inputStyle || {}]"
				:value="formattedValue"
				:placeholder="theme.floating_label ? ' ' : placeholder"
				:disabled="disabled"
				@input="changeEventValue"
				@keydown="(e) => emit('keydown', e)"
				@focus="(e) => emit('focus', e)"
				@blur="(e) => emit('blur', e)"
			/>
			<label
				v-if="type === 'color'"
				:class="[theme.end_icon, 'border-gray-300 pointer-events-auto rounded-md border']"
				:style="{
					backgroundColor: `${formattedValue || '#000000'}`
				}"
			>
				<input
					type="color"
					class="h-full w-full cursor-pointer opacity-0"
					@change="(e: any) => changeValue(e.target.value)"
				/>
			</label>
			<input
				v-if="['select', 'autocomplete'].includes(type)"
				:value="formattedValue"
				type="text"
				:class="[
					theme.input,
					'pointer-events-none absolute left-0 right-0 top-0 z-[-1] select-none opacity-0',
					inputDirectionClass,
					inputClass
				]"
				:style="inputStyle"
				disabled
				:placeholder="theme.floating_label ? ' ' : placeholder"
			/>
			<select
				v-if="['select'].includes(type)"
				ref="inputEl"
				:name="name"
				:autocomplete="autocomplete"
				:value="formattedValue"
				:class="['flex-grow', theme.input, inputClass]"
				:style="inputStyle"
				:disabled="disabled"
				@change="changeEventValue"
				@focus="(e) => emit('focus', e)"
				@blur="(e) => emit('blur', e)"
			>
				<option v-if="clearable" :value="null" default selected class="text-red-500 text-xs">
					{{ selectedOption?.[optionValue] || selectedOption ? $t("clear_selection") : "" }}
				</option>
				<option
					v-for="(option, i) in options"
					:key="i"
					:value="optionValue ? (option as any)[optionValue] : option"
					:selected="optionValue ? (option as any)[optionValue] === modelValue : option === modelValue"
					@change="changeEventValue"
				>
					{{ optionName ? (option as any)[optionName] : option }}
				</option>
			</select>
			<input
				v-if="['autocomplete'].includes(type)"
				ref="inputEl"
				:name="name"
				:class="['flex-grow', theme.input, inputDirectionClass, inputClass]"
				style="inputStyle"
				inputmode="tel"
				autocomplete="search"
				:disabled="disabled"
				:value="
					state.isFocused
						? state.searchText
						: optionDisplay
							? selectedOption?.[optionDisplay]
							: selectedOption
				"
				:placeholder="theme.floating_label ? ' ' : placeholder"
				@input="(e: any) => (state.searchText = e.target.value)"
				@focus="(e) => changeFocus(true, e)"
				@blur="(e) => changeFocus(false, e)"
			/>
			<div
				v-if="['autocomplete'].includes(type) && state.isFocused"
				class="text-input-autocomplete-list bg-white absolute left-0 right-0 z-[2] mt-12 max-h-40 w-full overflow-y-auto overscroll-contain rounded-md py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
				@wheel.stop
			>
				<div v-if="state.searching">
					<div class="flex items-center justify-center px-4 py-8">
						<Icon name="svg-spinners:tadpole" class="h-7 w-7 text-company" />
					</div>
				</div>
				<div v-else-if="!filteredOptions?.length">
					<div
						:class="[
							'text-slate-300 flex items-center justify-center truncate px-4 py-8 text-center text-lg font-bold',
							emptyBoxClass
						]"
					>
						{{ $t("nothing_found") }}
					</div>
				</div>
				<template v-else>
					<div
						v-if="selectedOption && clearable"
						@pointerdown.prevent
						@click="
							() => {
								changeValue(null)
								inputEl?.blur()
							}
						"
					>
						<slot name="option" :option="null">
							<div class="px-1 pb-1">
								<div
									class="text-red-500 flex w-full cursor-pointer items-center truncate rounded-md bg-red-500/10 px-3 py-2 text-xs"
								>
									<span>
										{{ $t("clear_selection") }}
									</span>
								</div>
							</div>
						</slot>
					</div>
					<div class="flex flex-col space-y-1 px-1 pb-0">
						<div
							v-for="(option, i) in filteredOptions"
							:key="i"
							@pointerdown.prevent
							@click="
								(e) => {
									changeValue(optionValue ? (option as any)[optionValue] : option)
									inputEl?.blur()
								}
							"
						>
							<slot
								name="option"
								:selected="formattedValue === (optionValue ? (option as any)[optionValue] : option)"
								:option="option"
							>
								<div
									class="flex w-full cursor-pointer items-center truncate rounded-md px-2 py-[7px] text-sm"
									:class="{
										'text-white bg-company':
											formattedValue === (optionValue ? (option as any)[optionValue] : option),
										'bg-white text-slate-800 hover:bg-company/10':
											formattedValue !== (optionValue ? (option as any)[optionValue] : option)
									}"
								>
									<span>
										{{ optionName ? (option as any)[optionName] : option }}
									</span>
								</div>
							</slot>
						</div>
					</div>
				</template>
			</div>
			<div
				v-if="['date'].includes(type)"
				class="absolute bottom-0 left-0 right-0 top-0 h-full w-full cursor-pointer"
				@click="state.isDatePickerVisible = true"
			/>
			<ClientOnly>
				<Teleport to="body">
					<DatePicker
						v-if="['date'].includes(type)"
						:element="state.id"
						color="rgb(var(--company-color))"
						:editable="true"
						:model-value="modelValue || value"
						format="YYYY/MM/DD"
						:type="dateType"
						:simple="dateSimple"
						:display-format="dateFormat"
						:disabled="disabled"
						:show="state.isDatePickerVisible"
						:locale="dateLocale"
						@close="state.isDatePickerVisible = false"
						@update:model-value="changeValue"
					/>
				</Teleport>
			</ClientOnly>
			<label
				v-if="placeholder && theme.floating_label"
				:class="['pointer-events-none max-h-8', theme.floating_label, floatingLabelClass]"
			>
				{{ placeholder }}
			</label>
			<Icon v-if="startIcon" :name="startIcon" :class="['pointer-events-none', theme.start_icon, iconClass]" />
			<Icon
				v-else-if="rightIcon"
				:name="rightIcon"
				:class="['pointer-events-none', theme.right_icon, iconClass]"
			/>
			<BaseIcon
				v-else-if="startBaseIcon"
				:name="startBaseIcon"
				:class="['pointer-events-none', theme.start_icon, iconClass]"
			/>
			<BaseIcon
				v-else-if="rightBaseIcon"
				:name="rightBaseIcon"
				:class="['pointer-events-none', theme.right_icon, iconClass]"
			/>
			<Icon v-if="endIcon" :name="endIcon" :class="['pointer-events-none', theme.end_icon, iconClass]" />
			<Icon v-else-if="leftIcon" :name="leftIcon" :class="['pointer-events-none', theme.left_icon, iconClass]" />
			<BaseIcon
				v-else-if="endBaseIcon"
				:name="endBaseIcon"
				:class="['pointer-events-none', theme.end_icon, iconClass]"
			/>
			<BaseIcon
				v-else-if="leftBaseIcon"
				:name="leftBaseIcon"
				:class="['pointer-events-none', theme.left_icon, iconClass]"
			/>
			<Icon
				v-else-if="['select'].includes(type)"
				name="mdi:chevron-down"
				:class="[
					'pointer-events-none',
					'duration-300 group-focus-within:rotate-180',
					theme.end_icon,
					iconClass
				]"
			/>
			<Icon
				v-else-if="['password'].includes(type) && state.isPasswordVisible"
				name="mdi:eye"
				:class="[
					'duration-300',
					theme.end_icon,
					iconClass,
					'pointer-events-auto cursor-pointer hover:text-company'
				]"
				@click="state.isPasswordVisible = false"
			/>
			<Icon
				v-else-if="['password'].includes(type) && !state.isPasswordVisible"
				name="mdi:eye-off"
				:class="[
					'duration-300',
					theme.end_icon,
					iconClass,
					'pointer-events-auto cursor-pointer hover:text-company'
				]"
				@click="state.isPasswordVisible = true"
			/>
			<slot name="overlay" v-bind="{ inputDirectionClass }"> </slot>
		</div>
		<p v-if="helperText" class="text-gray-500 mb-0 ms-3 mt-2 text-start text-xs dark:text-gray-400">
			{{ helperText }}
		</p>
	</div>
</template>

<script setup lang="ts" generic="T extends { [key: string]: any } | string">
import type { StyleValue } from "vue"
import type { IconType } from "~/types/base-icons"

const app = useNuxtApp()
const moment = useMoment()

const DatePicker = defineAsyncComponent(() => import("vue3-persian-datetime-picker"))

const emit = defineEmits<{
	"update:value": [any]
	"update:modelValue": [any]
	input: [Event]
	keydown: [KeyboardEvent]
	change: [any]
	focus: [Event]
	blur: [Event]
	overlay: []
}>()
const props = withDefaults(
	defineProps<{
		label?: string
		placeholder?: string
		modelValue?: any
		value?: any
		status?: "normal" | "success" | "danger" | "black"
		themeType?: "standard" | "filled" | "outlined" | "default"
		type?: "text" | "number" | "select" | "date" | "textarea" | "color" | "password" | "autocomplete"
		startIcon?: string
		rightIcon?: string
		endIcon?: string
		leftIcon?: string
		startBaseIcon?: IconType
		rightBaseIcon?: IconType
		endBaseIcon?: IconType
		leftBaseIcon?: IconType
		options?: T[]
		optionValue?: any
		optionName?: any
		optionDisplay?: any
		helperText?: string
		name?: string
		clearable?: boolean
		autocomplete?: string
		list?: string
		disabled?: boolean
		dateFormat?: string
		dateLocale?: "fa" | "en"
		inputClass?: unknown
		labelClass?: unknown
		floatingLabelClass?: unknown
		iconClass?: unknown
		inputStyle?: StyleValue
		char?: number
		size?: "md" | "lg"
		fetchFunction?: (text: string) => Promise<void>
		dateType?: "date" | "datetime" | "year-month" | "month"
		dateSimple?: boolean
		emptyBoxClass?: unknown
		format?: "thousand-separated"
	}>(),
	{
		type: "text",
		dateLocale: "fa",
		dateType: "date",
		size: "md",
		clearable: true
	}
)

const state = reactive({
	isPasswordVisible: false,
	isDatePickerVisible: false,
	date: "",
	id: undefined as string | undefined,
	searchText: "",
	isFocused: false,
	searching: false
})

const inputEl = ref<HTMLElement>()

const inputDirectionClass = computed(() => {
	if (!props.modelValue && !props.value) return ""

	const v =
		props.type == "autocomplete"
			? state.isFocused
				? state.searchText
				: props.optionDisplay
					? selectedOption.value?.[props.optionDisplay]
					: selectedOption
			: String(props.value || props.modelValue || "")
	const c = v?.substr?.(0, 1)
	const p = /^[\u0600-\u06FF\s]+$/
	if (!p.test(c)) {
		return "dir-ltr"
	} else {
		return "dir-rtl"
	}
})

const inputType = computed(() => {
	if (props.type === "number") return "text"
	if (props.type === "color") return "text"
	if (props.type === "date") return "text"
	if (props.type === "password" && state.isPasswordVisible) return "text"
	if (props.type === "password" && !state.isPasswordVisible) return "password"
	return props.type
})

const themes = computed(() => ({
	standard: {
		input: [
			"block py-2.5 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 peer",
			{
				"border-gray-300 focus:border-company": !props.status || props.status === "normal",
				"border-green-500": props.status === "success",
				"border-red-500": props.status === "danger",
				"border-slate-500": props.status === "black"
			},
			props.rightIcon || props.rightBaseIcon
				? "pr-9"
				: props.startIcon || props.startBaseIcon
					? "ltr:pl-9 rtl:pr-9"
					: "ltr:pl-0 rtl:pr-0",
			props.leftIcon || props.leftBaseIcon
				? "pl-9"
				: props.endIcon || props.endBaseIcon || ["select", "color", "password"].includes(props.type)
					? "rtl:pl-9"
					: "rtl:pl-0"
		],
		input_dark: [
			"dark:text-white",
			{
				"dark:border-gray-600 dark:focus:border-company": !props.status || props.status === "normal"
			}
		],
		floating_label: [
			"absolute text-sm duration-300 transform top-0 z-[1] origin-left rtl:origin-right",
			"scale-75 peer-focus:scale-75 -translate-y-1/2 peer-focus:-translate-y-1/2 mt-0 peer-focus:mt-0",
			"peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:mt-2.5",
			{
				"text-gray-500 peer-focus:text-company ": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			},
			props.rightIcon || props.rightBaseIcon
				? "right-9"
				: props.startIcon || props.startBaseIcon
					? "start-9"
					: "start-0"
		],
		floating_label_dark: [
			{
				"dark:text-gray-400 peer-focus:dark:text-company": !props.status || props.status === "normal"
			}
		],
		label: ["mb-2 text-sm font-bold text-gray-900 flex items-center"],
		label_dark: "dark:text-white",
		start_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none start-0",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		end_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none end-0",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		left_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none left-0",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		right_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none right-0",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		]
	},
	filled: {
		input: [
			"block rounded-t-lg pb-2.5 pt-5 w-full text-sm text-gray-900 bg-gray-50 border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 peer",
			{
				"border-gray-300 focus:border-company": !props.status || props.status === "normal",
				"border-green-500": props.status === "success",
				"border-red-500": props.status === "danger",
				"border-slate-500": props.status === "black"
			},
			props.rightIcon || props.rightBaseIcon
				? "pr-12"
				: props.startIcon || props.startBaseIcon
					? "ltr:pl-12 rtl:pr-12"
					: "ltr:pl-2.5 rtl:pr-2.5",

			props.leftIcon || props.leftBaseIcon
				? "pl-12"
				: props.endIcon || props.endBaseIcon || ["select", "color", "password"].includes(props.type)
					? "ltr:pr-12 rtl:pl-12"
					: "ltr:pr-2.5 rtl:pl-2.5"
		],
		input_dark: [
			"dark:bg-gray-700 dark:text-white",
			{
				"dark:border-gray-600 dark:focus:border-company": !props.status || props.status === "normal"
			}
		],
		floating_label: [
			"absolute text-sm duration-300 transform z-[1] origin-left rtl:origin-right top-4",
			"scale-75 peer-focus:scale-75 -translate-y-4 peer-focus:-translate-y-4 mt-0 peer-focus:mt-0",
			"peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:mt-0",
			{
				"text-gray-500 peer-focus:text-company ": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			},
			props.rightIcon || props.rightBaseIcon
				? "right-12"
				: props.startIcon || props.startBaseIcon
					? "start-12"
					: "start-2.5"
		],
		floating_label_dark: [
			{
				"dark:text-gray-400 peer-focus:dark:text-company": !props.status || props.status === "normal"
			}
		],
		label: ["mb-2 text-sm font-bold text-gray-900 flex items-center"],
		label_dark: "dark:text-white",
		start_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none start-2.5",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		end_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none end-2.5",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		left_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none left-2.5",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		right_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none right-2.5",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		]
	},
	outlined: {
		input: [
			"block w-full text-sm text-gray-900 bg-transparent rounded-lg border-2 appearance-none focus:outline-none focus:ring-0 peer duration-300 outline-none",
			{
				"border-gray-300 focus:border-company": !props.status || props.status === "normal",
				"border-green-500": props.status === "success",
				"border-red-500": props.status === "danger",
				"border-slate-500": props.status === "black"
			},
			{
				"py-3": props.size == "md",
				"py-4": props.size == "lg"
			},
			props.rightIcon || props.rightBaseIcon
				? "pr-12"
				: props.startIcon || props.startBaseIcon
					? "ltr:pl-12 rtl:pr-12"
					: "ltr:pl-2.5 rtl:pr-2.5",
			props.leftIcon || props.leftBaseIcon
				? "pl-12"
				: props.endIcon || props.endBaseIcon || ["select", "color", "password"].includes(props.type)
					? "ltr: pr-12 rtl:pl-12"
					: "ltr:pr-2.5 rtl:pl-2.5"
		],
		input_dark: [
			"dark:text-white",
			{
				"dark:border-gray-600 dark:focus:border-company": !props.status || props.status === "normal"
			}
		],
		floating_label: [
			"absolute text-sm duration-300 transform z-[1] origin-left rtl:origin-right bg-white px-2 top-0",
			"peer-focus:scale-75 scale-75 -translate-y-1/2 peer-focus:-translate-y-1/2 mt-0 peer-focus:mt-0",
			"peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0",
			{
				"text-gray-500 peer-focus:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			},
			{
				"peer-placeholder-shown:mt-[13px]": props.size == "md",
				"peer-placeholder-shown:mt-[17px]": props.size == "lg"
			},
			props.rightIcon || props.rightBaseIcon
				? "right-10"
				: props.startIcon || props.startBaseIcon
					? "start-10"
					: "start-1"
		],
		floating_label_dark: [
			"dark:bg-gray-900",
			{
				"dark:text-gray-400 peer-focus:dark:text-company": !props.status || props.status === "normal"
			}
		],
		label: ["mb-2 text-sm font-bold text-gray-900 flex items-center"],
		label_dark: "dark:text-white",
		start_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none start-2.5 duration-300",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		end_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none end-2.5 duration-300",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		left_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none left-2.5 duration-300",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		right_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none right-2.5 duration-300",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		]
	},
	default: {
		input: [
			"bg-white border-2 text-gray-900 duration-300 text-sm rounded-lg  block w-full py-3 appearance-none",
			{
				"border-gray-300 outline-company/0 focus:outline-company/50 focus:border-company":
					!props.status || props.status === "normal",
				"focus:outline-green-500/50 border-green-500": props.status === "success",
				"focus:outline-red-500/50 border-red-500": props.status === "danger",
				"focus:outline-slate-500/50 border-slate-500": props.status === "black"
			},
			props.rightIcon || props.rightBaseIcon
				? "pr-12"
				: props.startIcon || props.startBaseIcon
					? "ltr:pl-12 rtl:pr-12"
					: "ltr:pl-2.5 rtl:pr-2.5",
			props.leftIcon || props.leftBaseIcon
				? "pl-12"
				: props.endIcon || props.endBaseIcon || ["select", "color", "password"].includes(props.type)
					? "ltr:pr-12 rtl:pl-12"
					: "ltr:pr-2.5 rtl:pl-2.5"
		],
		input_dark: [
			"dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white",
			{
				"dark:border-gray-600 dark:focus:outline-company dark:focus:border-company":
					!props.status || props.status === "normal"
			}
		],
		floating_label: null,
		floating_label_dark: null,
		label: ["mb-2 text-sm font-bold text-gray-900 flex items-center"],
		label_dark: "dark:text-white",
		start_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none  start-2.5",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		end_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none end-2.5",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		left_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto pointer-events-none  left-2.5",
			{
				"text-gray-900 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		],
		right_icon: [
			"absolute top-0 bottom-0 w-6 h-6 my-auto transform pointer-events-none right-2.5",
			{
				"text-gray-600 group-focus-within:text-company": !props.status || props.status === "normal",
				"text-green-500": props.status === "success",
				"text-red-500": props.status === "danger",
				"text-slate-500": props.status === "black"
			}
		]
	}
}))

const theme = computed(() => themes.value[props.themeType || "default"])

const valueDate = computed(() =>
	props.modelValue || props.value
		? moment(props.modelValue || props.value, "YYYY/MM/DD")
				.utc(true)
				.local(false)
				.format(
					props.dateLocale === "fa" ? props.dateFormat || "jYYYY/jMM/jDD" : props.dateFormat || "YYYY/MM/DD"
				)
		: null
)

const filteredOptions = computed(() =>
	props.fetchFunction
		? props.options
		: props.options?.filter((o) => {
				const on = String(props.optionName ? (o as any)?.[props.optionName] : o || 0).toLocaleLowerCase()
				const od = String(props.optionDisplay ? (o as any)?.[props.optionDisplay] : o || 0).toLocaleLowerCase()
				const ov = String(props.optionValue ? (o as any)?.[props.optionValue] : o || 0).toLocaleLowerCase()
				const s = String(state.searchText || "").toLocaleLowerCase()
				return on.includes(s) || od.includes(s) || ov.includes(s)
			}) || []
)

const selectedOption = computed(
	() =>
		filteredOptions.value?.find(
			(o: any) => (props.optionValue ? o[props.optionValue] : o) == (props.modelValue || props.value)
		) ||
		props.options?.find(
			(o: any) => (props.optionValue ? o[props.optionValue] : o) == (props.modelValue || props.value)
		) ||
		props.modelValue ||
		props.value ||
		null
)

const formattedValue = computed(() => {
	if (["date"].includes(props.type)) {
		return valueDate.value
	}
	let v = props.modelValue || props.value
	if (props.format == "thousand-separated") {
		v = app.$stringUtil.thousandFormat(v)
	}
	return v
})
watch(
	() => [props.modelValue, props.value, state.isFocused],
	() => {
		if (state.isFocused) {
			state.searchText = ""
		} else {
			state.searchText = props.optionDisplay ? selectedOption.value?.[props.optionDisplay] : selectedOption.value
		}
	},
	{
		immediate: true
	}
)

watch(
	() => [state.searchText, state.isFocused],
	() => {
		if (!props.fetchFunction) return
		if (!state.isFocused) return
		state.searching = true
	}
)

watchDebounced(
	() => [state.searchText, state.isFocused],
	async () => {
		if (!props.fetchFunction) return
		if (!state.isFocused) return
		try {
			await props.fetchFunction(state.searchText)
		} catch (e) {}
		state.searching = false
	},
	{
		debounce: 1200
	}
)

const changeFocus = (focus: boolean, event?: Event) => {
	state.isFocused = focus
	if (focus && event) {
		emit("focus", event)
	} else if (!focus && event) {
		emit("blur", event)
	}
}

const changeEventValue = (e: any) => {
	let v = e.target.value
	if (v == app.$i18n.t("clear_selection") && props.clearable) {
		v = null
	}
	if (v == null && e.target.value) {
		e.target.blur()
	}
	if (props.format == "thousand-separated") {
		v = v?.replace?.(/,/g, "")
	}
	if (props.type === "number") {
		if (isNaN(Number(v))) {
			e.target.value = e.target._value || ""
			return
		}
	}
	if (props.format == "thousand-separated") {
		e.target.value = app.$stringUtil.thousandFormat(v)
	}
	emit("update:value", v)
	emit("update:modelValue", v)
	emit("change", v)
	emit("input", e)
}

const changeValue = (v: any) => {
	emit("update:value", v)
	emit("update:modelValue", v)
	emit("change", v)
}

onMounted(() => {
	state.id = `textinput-${app._uid}`
})
</script>

<style>
@layer {
	.vpd-prev svg,
	.vpd-next svg {
		@apply mx-auto;
	}
	.vpd-down-arrow-btn svg {
		@apply mx-auto;
	}
	.vpd-up-arrow-btn svg {
		@apply mx-auto;
	}
	.vpd-header {
		@apply rounded-t-[23px];
	}
}

/* Hide the dropdown arrow */
.text-inner-input::-webkit-calendar-picker-indicator {
	display: none !important;
	-webkit-appearance: none;
}
.text-inner-input input {
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
}

.text-input-autocomplete-list {
	scrollbar-color: #091e4224;
	scrollbar-width: thin;
}
.text-input-autocomplete-list::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}
.text-input-autocomplete-list::-webkit-scrollbar-track-piece {
	background: #091e420f;
}
</style>
